// Shared helpers for the chat components: time / size / duration formatting, rich i18n templates, pressable spans.
import { Fragment, type KeyboardEvent, type ReactNode, type SyntheticEvent } from 'react';
import type { UiT } from '../i18n/index.tsx';

const fmtCache = new Map<string, Intl.DateTimeFormat>();

/** App-wide 12/24-hour preference (User Settings › Dil); null = the locale default. */
let hourCycle: 'h12' | 'h23' | null = null;
export function setUiHourCycle(cycle: 'h12' | 'h23' | null): void {
  hourCycle = cycle;
}

function dtf(locale: string, base: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const opts: Intl.DateTimeFormatOptions = hourCycle && (base.hour || base.timeStyle) ? { ...base, hourCycle } : base;
  const key = locale + JSON.stringify(opts);
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, opts);
    fmtCache.set(key, f);
  }
  return f;
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isYesterday(date: Date, now: Date): boolean {
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  return isSameDay(date, y);
}

/** "21:42" (tr) / "9:42 PM" (en). */
export function formatClock(date: Date, locale: string): string {
  return dtf(locale, { hour: 'numeric', minute: '2-digit' }).format(date);
}

/** Message header time: "Bugün 21:42", "Dün 18:05", otherwise a short date + time. */
export function formatMessageTime(date: Date, locale: string, t: UiT, now: Date = new Date()): string {
  const clock = formatClock(date, locale);
  if (isSameDay(date, now)) return t('chat.time.today', { time: clock });
  if (isYesterday(date, now)) return t('chat.time.yesterday', { time: clock });
  return dtf(locale, { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

/** Full timestamp for tooltips ("12 Ekim 2026 21:42"). */
export function formatFullTime(date: Date, locale: string): string {
  return dtf(locale, { dateStyle: 'long', timeStyle: 'short' }).format(date);
}

/** Day divider label: "Bugün", "Dün", otherwise "12 Ekim 2026". */
export function formatDayLabel(date: Date, locale: string, t: UiT, now: Date = new Date()): string {
  if (isSameDay(date, now)) return t('chat.day.today');
  if (isYesterday(date, now)) return t('chat.day.yesterday');
  return dtf(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];
/** 2516582 → "2,4 MB" (tr) / "2.4 MB" (en). */
export function formatFileSize(bytes: number, locale: string): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < UNITS.length - 1) {
    v /= 1024;
    i++;
  }
  const digits = i === 0 || v >= 10 ? 0 : 1;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(v) + ' ' + UNITS[i];
}

/** 24 → "0:24", 3725 → "1:02:05". */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/**
 * Renders a translated template whose `{placeholders}` are React nodes:
 * `rich(t('chat.typing.two'), { a: <b>Mert</b>, b: <b>Alara</b> })`.
 * Call `t` without the node vars so the placeholders survive interpolation.
 */
export function rich(template: string, nodes: Record<string, ReactNode>): ReactNode {
  const parts = template.split(/\{(\w+)\}/g);
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <Fragment key={i}>{p in nodes ? nodes[p] : `{${p}}`}</Fragment>
    ) : p ? (
      <Fragment key={i}>{p}</Fragment>
    ) : null,
  );
}

/** Props that turn a span/div into a keyboard-accessible button (keeps design-system markup intact). */
export function pressable(
  onPress: ((e: SyntheticEvent) => void) | undefined,
  opts: { disabled?: boolean } = {},
) {
  if (!onPress) return {};
  return {
    role: 'button' as const,
    tabIndex: opts.disabled ? -1 : 0,
    'aria-disabled': opts.disabled || undefined,
    onClick: (e: SyntheticEvent) => {
      if (!opts.disabled) onPress(e);
    },
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!opts.disabled) onPress(e);
      }
    },
  };
}
